/**
 * API правки карточки площадки из окна (решение владельца 11.10.2026).
 *
 * Правка сначала ложится в ПИМ (новая ревизия со слоем правок + запись журнала
 * `card_edit`), а на площадку уходит тем же путём, что любая правка журнала.
 * Контракт — `services/marketplace/src/services/marketplace-card-window-edit.service.ts`:
 *   GET  /marketplace/sellers/:sellerId/cards/:cardId/window-state  (Card:READ)
 *   POST /marketplace/sellers/:sellerId/cards/:cardId/window-edit   (Card:UPDATE)
 * `cardId` здесь — `marketplace_cards.id` (его даёт присутствие артикула).
 * Цена окном не правится. Бренд и габариты правятся там, где `window-state`
 * отдаёт `editable` (ВБ — бренд и габариты, ОЗОН — только бренд, Маркет — ничего);
 * штрихкод и артикул продавца не правятся нигде (штрихкод ОЗОН выдаёт площадка).
 */

import { api } from '../../../../shared/api/client'

const unwrap = (response: any): any => response?.data?.data ?? response?.data ?? {}

export interface WindowEditableField<T> {
  /** Значение ПИМ с учётом правок. */
  value: T
  /** Что сейчас на витрине. */
  platform_value: T | null
  overridden: boolean
}

/** Реквизит карточки (бренд, габариты, штрихкод): значение плюс «можно ли править здесь». */
export interface WindowRequisite<T> extends WindowEditableField<T> {
  editable: boolean
  /** Почему поле закрыто на этой площадке; null — открыто. */
  locked_reason: string | null
}

/** Габариты с единицами: ВБ и Маркет — см и кг, ОЗОН — мм и г (только просмотр). */
export interface WindowDimensions {
  length: number | null
  width: number | null
  height: number | null
  weight: number | null
  length_unit: string
  weight_unit: string
}

export interface WindowCharacteristic extends WindowEditableField<unknown> {
  target_id: number | null
  name: string
  locked: boolean
  locked_reason: string | null
}

export interface WindowJournalRecord {
  id: number
  status: string
  field_keys: string[]
  field_labels: string[]
  value_before: unknown
  value_after: unknown
  changed_at: string
  changed_by: number | null
  changed_by_name: string | null
  origin: string | null
}

export interface CardWindowState {
  card: {
    id: number
    article: string
    marketplace: string
    seller_id: number
    external_id: string | null
    status: string
    cabinet: string | null
    price: number | null
  }
  linked: boolean
  editable: boolean
  editable_reason: string | null
  editable_code: string | null
  mirror_found: boolean
  mirror_synced_at: string | null
  revision: number | null
  has_manual_overrides: boolean
  fields: {
    title: WindowEditableField<string>
    description: WindowEditableField<string>
    media_urls: WindowEditableField<string[]>
    characteristics: WindowCharacteristic[]
    brand: WindowRequisite<string>
    dimensions: WindowRequisite<WindowDimensions | null>
    barcode: WindowRequisite<string>
  }
  send: {
    enabled: boolean
    wb_text_enabled: boolean
    ym_write_enabled: boolean
  }
  history: {
    journal: WindowJournalRecord[]
    sends: unknown[]
  }
}

/** Правка габаритов: любое непустое подмножество осей; длины — целые см, вес — кг. */
export type WindowDimensionsChange = Partial<Record<'length' | 'width' | 'height' | 'weight', number>>

export interface WindowEditChanges {
  title?: string
  description?: string
  characteristics?: Record<string, string | string[]>
  media_urls?: string[]
  brand?: string
  dimensions?: WindowDimensionsChange
}

export interface WindowEditRequest {
  changes: WindowEditChanges
  send?: boolean
  publish_confirmed?: boolean
  dry_run?: boolean
}

export interface WindowEditResult {
  card_id: number
  revisionId: number | null
  revision: number | null
  base_created: boolean
  sourceChangeId: number | null
  affected: number
  field_keys: string[]
  send: boolean
  dry_run: boolean
  /** Есть только при отправке: фоновая задача разворота и отправки. */
  jobId: string | null
}

/** Итог фоновой задачи (metadata прогресса). */
export type WindowEditJobStatus =
  | 'sent'
  | 'dry_run'
  | 'channel_disabled'
  | 'transport_disabled'
  | 'failed'
  | 'stale'
  | 'skipped'
  | 'nothing_to_send'

export interface WindowEditJobOutcome {
  status: WindowEditJobStatus
  message: string | null
  reason: string | null
  card_id: number | null
  source_change_id: number | null
  change_id: number | null
  send_enabled: boolean | null
}

/** Подписи итога отправки: что случилось с правкой, словами для человека. */
export const WINDOW_EDIT_OUTCOME_LABELS: Record<WindowEditJobStatus, { label: string; tone: 'ok' | 'warn' | 'bad' | 'info' }> = {
  sent: { label: 'Отправлено на площадку', tone: 'ok' },
  dry_run: { label: 'Пробный прогон: на площадку не отправлялось', tone: 'info' },
  channel_disabled: { label: 'Отправка выключена: правка сохранена в ПИМ и журнале', tone: 'warn' },
  transport_disabled: { label: 'Запись на площадку выключена: правка сохранена в ПИМ и журнале', tone: 'warn' },
  failed: { label: 'Площадка отклонила правку', tone: 'bad' },
  stale: { label: 'Карточка на площадке изменилась: правку нужно проверить заново', tone: 'warn' },
  skipped: { label: 'Отправка пропущена', tone: 'warn' },
  nothing_to_send: { label: 'Отправлять нечего: площадка уже содержит эти значения', tone: 'info' },
}

export const parseWindowEditOutcome = (metadata: unknown): WindowEditJobOutcome | null => {
  if (!metadata || typeof metadata !== 'object') return null
  const raw = metadata as Record<string, unknown>
  const status = raw.status
  if (typeof status !== 'string' || !(status in WINDOW_EDIT_OUTCOME_LABELS)) return null
  const text = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value.trim() : null
  const num = (value: unknown): number | null =>
    Number.isFinite(Number(value)) && value !== null && value !== '' ? Number(value) : null
  return {
    status: status as WindowEditJobStatus,
    message: text(raw.message),
    reason: text(raw.reason),
    card_id: num(raw.card_id),
    source_change_id: num(raw.source_change_id),
    change_id: num(raw.change_id),
    send_enabled: typeof raw.send_enabled === 'boolean' ? raw.send_enabled : null,
  }
}

/** Коды отказов окна с понятными формулировками (бэкенд даёт и свой текст — он важнее). */
export const WINDOW_EDIT_ERROR_HINTS: Record<string, string> = {
  CARD_NOT_LINKED: 'Карточка не связана с ПИМ. Свяжите её в разделе «Связывание с PIM».',
  CARD_NOT_PUBLISHED: 'Карточка не опубликована на площадке или исключена из выгрузки: править её из окна нельзя.',
  CARD_IN_FLIGHT: 'Карточка сейчас отправляется на площадку. Дождитесь завершения.',
  MIRROR_NOT_FOUND: 'Копии карточки с площадки в ПИМ нет. Сверьте карточку с площадкой и повторите.',
  NO_CHANGES: 'Изменений нет.',
  FIELD_NOT_EDITABLE: 'Это поле на этой площадке из окна не правится. Ничего не сохранено.',
  VALIDATION_ERROR: 'Значение не принято: проверьте введённые данные.',
  CHARACTERISTIC_LOCKED: 'Эту характеристику из окна менять нельзя (склейка или нет справочника).',
  CHARACTERISTIC_UNKNOWN: 'Такой характеристики нет в справочнике площадки.',
}

export interface WindowEditErrorInfo {
  code: string | null
  message: string
}

export const windowEditErrorInfo = (error: any, fallback: string): WindowEditErrorInfo => {
  const body = error?.response?.data?.error
  const code = typeof body?.code === 'string' && body.code ? body.code : null
  const message =
    (typeof body?.message === 'string' && body.message) ||
    (code ? WINDOW_EDIT_ERROR_HINTS[code] : '') ||
    error?.message ||
    fallback
  return { code, message }
}

const normalizeRequisite = <T,>(raw: any, empty: T): WindowRequisite<T> => {
  if (!raw || typeof raw !== 'object') {
    return { value: empty, platform_value: null, overridden: false, editable: false, locked_reason: null }
  }
  return {
    value: raw.value ?? empty,
    platform_value: raw.platform_value ?? null,
    overridden: Boolean(raw.overridden),
    editable: raw.editable === true,
    locked_reason: typeof raw.locked_reason === 'string' && raw.locked_reason ? raw.locked_reason : null,
  }
}

const normalizeState = (raw: any): CardWindowState => {
  const fields = raw?.fields ?? {}
  return {
    ...raw,
    fields: {
      title: fields.title ?? { value: '', platform_value: null, overridden: false },
      description: fields.description ?? { value: '', platform_value: null, overridden: false },
      media_urls: fields.media_urls ?? { value: [], platform_value: [], overridden: false },
      characteristics: Array.isArray(fields.characteristics) ? fields.characteristics : [],
      brand: normalizeRequisite<string>(fields.brand, ''),
      dimensions: normalizeRequisite<WindowDimensions | null>(fields.dimensions, null),
      barcode: normalizeRequisite<string>(fields.barcode, ''),
    },
    send: raw?.send ?? { enabled: false, wb_text_enabled: false, ym_write_enabled: false },
    history: {
      journal: Array.isArray(raw?.history?.journal) ? raw.history.journal : [],
      sends: Array.isArray(raw?.history?.sends) ? raw.history.sends : [],
    },
  } as CardWindowState
}

export const cardWindowApi = {
  async getState(sellerId: number, cardId: number, signal?: AbortSignal): Promise<CardWindowState> {
    const response = await api.get(`/marketplace/sellers/${sellerId}/cards/${cardId}/window-state`, { signal })
    return normalizeState(unwrap(response))
  },

  /**
   * `send=true` требует `publish_confirmed=true` (бэкенд иначе отклоняет):
   * подтверждение получено в диалоге окна до вызова.
   */
  async edit(sellerId: number, cardId: number, request: WindowEditRequest): Promise<WindowEditResult> {
    const response = await api.post(`/marketplace/sellers/${sellerId}/cards/${cardId}/window-edit`, request)
    const data = unwrap(response)
    const jobId = typeof data.jobId === 'string' && data.jobId.trim() ? data.jobId.trim() : null
    if (request.send && !jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return {
      card_id: Number(data.card_id ?? cardId),
      revisionId: data.revisionId ?? null,
      revision: data.revision ?? null,
      base_created: Boolean(data.base_created),
      sourceChangeId: data.sourceChangeId ?? null,
      affected: Number(data.affected ?? 0),
      field_keys: Array.isArray(data.field_keys) ? data.field_keys : [],
      send: Boolean(data.send),
      dry_run: Boolean(data.dry_run),
      jobId,
    }
  },
}
